// @vendor
const Immutable = require('immutable');
const { Promise } = require('es6-promise');
const padRight = require('lodash/string/padRight');

// @actions
const signaturePatternActions = require('actions/signaturePattern');
const { getFiocStatus } = require('actions/fiocDocumentation');

// @core
const apiURLBuilder = require('core/apiURLBuilder');

// @utilities
const { APIPost } = require('utilities/APIRequestHelper');
const DateHelper = require('utilities/dateHelper');
const { getAddress, getPhoneCodeFromOriginCountry } = require('utilities/personalDataHelper');
const { getSessionId, getWidgetData } = require('utilities/nuDataUtils');

// @constants
const actionTypes = require('constants/actionTypes');
const countryMandatoryLength = require('constants/countryMandatoryLength');
const {
  COUNTRIES_CODES: { ES },
} = require('constants/countries');
const PROVINCES = require('constants/provinces');
const { getTranslatedCountry } = require('constants/countries');
const { getTranslatedRoad } = require('constants/typeOfRoads');
const {
  PREFIX_SPANISH_ISIN_CODE,
  PROFILE_EDITOR_UPDATE_OTP_CONTACT_TYPE,
  PROFILE_EDITOR_UPDATE_OTP_ON_EMAIL,
} = require('constants/index');

const initStep = profile => ({
  type: actionTypes.SET_PROFILE_INIT_STEP,
  payload: {
    profile,
  },
});

const reset = () => ({
  type: actionTypes.PROFILE_EDIT_RESET,
});

const setProfileEditorInProgress = () => ({
  type: actionTypes.SET_PROFILE_EDIT_REQUEST,
});

const setProfileEditorSuccess = () => ({
  type: actionTypes.SET_PROFILE_EDIT_SUCCESS,
});

const setSuccessFiscalData = countries => ({
  type: actionTypes.SET_FISCAL_DATA,
  countries,
});

const setProfileEditorFailure = error => ({
  type: actionTypes.SET_PROFILE_EDIT_FAILURE,
  payload: {
    error,
  },
});

const setVisibleStep = step => ({
  type: actionTypes.PROFILE_EDIT_SET_VISIBLE_STEP,
  payload: {
    step,
  },
});

const validateStep = (step, profile = null) => ({
  type: actionTypes.PROFILE_EDIT_VALIDATE_STEP,
  payload: {
    profile,
    step,
  },
});

const setWillCancel = willCancel => ({
  type: actionTypes.PROFILE_EDIT_WILL_CANCEL,
  payload: {
    willCancel,
  },
});

const setWillLeave = () => ({
  type: actionTypes.PROFILE_EDIT_WILL_LEAVE,
});

const setField = (inputField, data) => ({
  type: actionTypes.PROFILE_EDIT_SET_FIELD,
  payload: {
    inputField,
    data,
  },
});

const updateAdditionalCountries = (index, data) => ({
  type: actionTypes.PROFILE_UPDATE_ADDITIONAL_TAX_COUNTRIES,
  payload: {
    index,
    data,
  },
});

const deleteAdditionalCountries = index => ({
  type: actionTypes.PROFILE_DELETE_ADDITIONAL_TAX_COUNTRIES,
  payload: {
    index,
  },
});

const actionChangeValue = data => ({
  type: actionTypes.PROFILE_ACTION_CHANGE_VALUE,
  payload: data,
});

const formatAddress = immProfile => {
  const {
    addressCountry,
    locality,
    numberStreet,
    postalCode,
    province,
    restOfAddress,
    streetName,
    typeOfRoads,
  } = getAddress(immProfile);

  const fields = [
    {
      value: typeOfRoads ? getTranslatedRoad(typeOfRoads) : '',
    },
    {
      value: streetName ? streetName : '',
    },
    {
      value: (numberStreet ? numberStreet : '') + ',',
    },
    {
      value: restOfAddress ? restOfAddress : '',
    },
    {
      value: locality ? locality + ',' : '',
    },
    {
      value: province ? PROVINCES[province] + ',' : '',
    },
    {
      value: addressCountry
        ? getTranslatedCountry(addressCountry)
        : getTranslatedCountry(PREFIX_SPANISH_ISIN_CODE),
    },
    {
      value: postalCode ? `(${postalCode})` : '',
    },
  ];

  return fields.map((field, index) => (index ? ` ${field.value}` : field.value)).join('');
};

const postAddressNewChange = () => (dispatch, getState) => {
  const parseListCountries = countries =>
    countries.map(c => ({
      paisTributacion: getTranslatedCountry(c.paisTributacion),
      tin: c.tin.codigodocumpersonacorp,
      textTin: c.textTin,
    }));

  const callback = (dispatch, getState) => {
    const profile = getState().profile;
    const step = getState().profileEditor.getIn(['steps', 0]);

    const immFormProfileAddressFields = Immutable.fromJS({
      codigoPostal: step.get('postalCode'),
      nombreDeVia: step.get('streetName').toUpperCase(),
      nombrePlaza: step.get('locality').toUpperCase(),
      numeroDeVia: step.get('country') == PREFIX_SPANISH_ISIN_CODE ? step.get('numberStreet') : '',
      paisDomicilio: step.get('country'),
      plaza: {
        plaza: {
          provincia: step.get('country') == PREFIX_SPANISH_ISIN_CODE ? step.get('province') : '',
        },
      },
      restoDomicilio: step.get('restOfAddress').toUpperCase(),
      tipoDeVia: step.get('country') == PREFIX_SPANISH_ISIN_CODE ? step.get('typeOfRoads') : '',
      residenceCountry: step.get('residenceCountry'),
    });

    const data = {
      query: {
        fechaDocumento: DateHelper.customFormat(new Date(), 'DD/MM/YYYY'),
        direccionHabitualFiscal: formatAddress(immFormProfileAddressFields),
        paisNacionalidad: getTranslatedCountry(profile.get('paisNacionalidad')),
        nombreCompleto: `${profile.get('nombreCliente').trim()} ${profile
          .get('apellidoUno')
          .get('apellido')
          .trim()} ${profile.get('apellidoDos').get('apellido').trim()}`,
        paisNacimiento: getTranslatedCountry(profile.get('paisNacimiento')),
        ciudadNacimiento: profile.get('plazaDeNacimiento').trim(),
        fechaNacimiento: DateHelper.customFormat(step.get('birthDay'), 'DD/MM/YYYY'),
        masResidenciasFiscales: step.get('moreFiscalResidences') == 'S',
        paisResidencia: getTranslatedCountry(step.get('residenceCountry')),
        numeroIdentificacionFiscal:
          step.get('residenceCountry') == PREFIX_SPANISH_ISIN_CODE
            ? step.get('nifNie') ||
              profile.get('documentoIdentificacion').get('codigodocumpersonacorp')
            : step.get('residenceTin') || step.get('textAreaTin'),
        textTinResidencia: step.get('textAreaTin'),
        listPaisesTributacionAdic:
          step.get('moreFiscalResidences') == 'S'
            ? parseListCountries(step.get('listPaisesTributacionAdic').toJS())
            : [],
      },
    };
    dispatch(setProfileEditorInProgress());
    APIPost(dispatch, apiURLBuilder.getURL('userProfileFatca'), data)
      .then(() => {
        dispatch(getFiocStatus()).then(() => dispatch(setProfileEditorSuccess()));
      })
      .catch(response => {
        dispatch(setProfileEditorFailure(response.error));
      });
  };

  signaturePatternActions.validateAndExecute(dispatch, getState, callback);
};

const postAddressChange = callbackRequestProfile => (dispatch, getState) => {
  const callback = (dispatch, getState, dataToken) => {
    new Promise(() => {
      const immStep = getState().profileEditor.getIn(['steps', 0]);
      const originCountry = getState().profile.get('originCountry');
      const esCountry = originCountry == ES;
      const requestData = {
        query: {
          tokenFirma: dataToken,
          nombreDeVia: immStep.get('streetName'),
          codigoPostal: immStep.get('postalCode'),
          nombreDePlaza: immStep.get('locality'),
          ...(esCountry
            ? {
                provincia: immStep.get('province'),
              }
            : {}),
          numeroDeVia: immStep.get('numberStreet'),
          restoDomicilio: immStep.get('restOfAddress'),
          ...(esCountry
            ? {
                tipoDeVia: immStep.get('typeOfRoads'),
              }
            : {}),
          context: getWidgetData(originCountry),
          nuDataSession: getSessionId(),
        },
      };
      dispatch(setProfileEditorInProgress());
      APIPost(dispatch, apiURLBuilder.getURL('userAddress'), requestData)
        .then(() => {
          callbackRequestProfile().then(() => {
            dispatch(setProfileEditorSuccess());
          });
        })
        .catch(response => {
          dispatch(setProfileEditorFailure(response.error));
        });
    });
  };

  new Promise(function () {
    signaturePatternActions.validateAndExecute(dispatch, getState, callback);
  });
};

const postEmailChange = callbackRequestProfile => (dispatch, getState) => {
  const callback = (dispatch, getState, dataToken) => {
    new Promise(() => {
      const immStep = getState().profileEditor.getIn(['steps', 0]);
      const requestData = {
        query: {
          tokenFirma: dataToken,
          email: immStep.get('email'),
          sessionId: immStep.get('uuid'),
          context: getWidgetData(),
          nuDataSession: getSessionId(),
        },
      };
      dispatch(setProfileEditorInProgress());
      APIPost(dispatch, apiURLBuilder.getURL('userEmail'), requestData)
        .then(() => {
          callbackRequestProfile().then(() => {
            dispatch(setProfileEditorSuccess());
          });
        })
        .catch(response => {
          dispatch(setProfileEditorFailure(response.error));
        });
    });
  };

  new Promise(function () {
    signaturePatternActions.validateAndExecute(dispatch, getState, callback);
  });
};

const postPhoneChange = callbackRequestProfile => (dispatch, getState) => {
  const callback = (dispatch, getState, dataToken) => {
    new Promise(() => {
      const immStep = getState().profileEditor.getIn(['steps', 0]);
      const originCountry = getState().profile.get('originCountry');
      const otpBySmsOrEmail = immStep.get('otpBySmsOrEmail');
      const phoneNumberWithoutFormat = immStep.get('phoneNumber').replace(/ /g, '');
      const numberPhone = `${getPhoneCodeFromOriginCountry(
        originCountry
      )}-${phoneNumberWithoutFormat.substr(0, 3)}${
        originCountry == ES ? '-' : ''
      }${phoneNumberWithoutFormat.substr(3)}`;

      const requestData = {
        query: {
          tokenFirma: dataToken,
          numero: numberPhone,
          context: getWidgetData(),
          nuDataSession: getSessionId(),
        },
      };

      if (otpBySmsOrEmail == PROFILE_EDITOR_UPDATE_OTP_ON_EMAIL) {
        requestData.query.contactType = PROFILE_EDITOR_UPDATE_OTP_CONTACT_TYPE;
      }

      dispatch(setProfileEditorInProgress());

      APIPost(dispatch, apiURLBuilder.getURL('userTelephone'), requestData)
        .then(() => {
          callbackRequestProfile().then(() => {
            dispatch(setProfileEditorSuccess());
          });
        })
        .catch(response => {
          dispatch(setProfileEditorFailure(response.error));
        });
    });
  };

  new Promise(function () {
    signaturePatternActions.validateAndExecute(dispatch, getState, callback);
  });
};

const postFiscalDataChange = callbackRequestProfile => (dispatch, getState) => {
  const callback = (dispatch, getState, dataToken) => {
    new Promise(() => {
      const immStep = getState().profileEditor.getIn(['steps', 0]);
      let countries = immStep.get('listPaisesTributacionAdic').toJS();

      countries.map((country, index) => {
        if (country.indTipoTributacion) {
          countries[index].indTipoTributacion = padRight(
            country.indTipoTributacion,
            countryMandatoryLength.indTipoTributacion
          );
        }
        if (country.tin.codigodocumpersonacorp) {
          countries[index].tin.codigodocumpersonacorp = padRight(
            country.tin.codigodocumpersonacorp,
            countryMandatoryLength.codigodocumpersonacorp
          );
        }
        if (country && country.id !== undefined) {
          delete countries[index].id;
        }
      });

      const requestData = {
        query: {
          tokenFirma: dataToken,
          provincia:
            immStep.get('birthCountry') !== PREFIX_SPANISH_ISIN_CODE
              ? '  '
              : immStep.get('birthProvince') || '  ',
          paisNacimiento: immStep.get('birthCountry'),
          plazaNacimiento: immStep.get('birthSquare'),
          listPaisesTributacionAdic: {
            tributAdicional: countries,
          },
          context: getWidgetData(),
          nuDataSession: getSessionId(),
        },
      };

      dispatch(setProfileEditorInProgress());

      APIPost(dispatch, apiURLBuilder.getURL('userFiscalData'), requestData)
        .then(() => {
          callbackRequestProfile().then(() => {
            dispatch(setSuccessFiscalData(countries));
            dispatch(setProfileEditorSuccess());
          });
        })
        .catch(response => {
          dispatch(setProfileEditorFailure(response.error));
        });
    });
  };

  new Promise(function () {
    signaturePatternActions.validateAndExecute(dispatch, getState, callback);
  });
};

const setEditingTaxAdditionalCountry = (value, index) => ({
  type: actionTypes.PROFILE_EDIT_EDITING_TAX_ADDITIONAL_COUNTRY_SET,
  payload: {
    index,
    value,
  },
});

const setAddingTaxAdditionalCountry = value => ({
  type: actionTypes.PROFILE_EDIT_ADDING_TAX_ADDITIONAL_COUNTRY_SET,
  payload: {
    value,
  },
});

const setEditMode = value => ({
  type: actionTypes.PROFILE_EDIT_SET_EDIT_MODE,
  payload: {
    value,
  },
});

module.exports = {
  actionChangeValue,
  deleteAdditionalCountries,
  postAddressChange,
  postAddressNewChange,
  postEmailChange,
  postFiscalDataChange,
  postPhoneChange,
  reset,
  setAddingTaxAdditionalCountry,
  setEditingTaxAdditionalCountry,
  setField,
  setEditMode,
  setVisibleStep,
  setWillCancel,
  setWillLeave,
  updateAdditionalCountries,
  validateStep,
  initStep,
};
